import { runMigrations } from './migrations';
import { SAVE_SCHEMA_VERSION } from './schema';
import type { STORE } from './schema';

/**
 * Minimal promise wrapper around IndexedDB.
 *
 * Deliberately tiny: the game needs key/value access to five stores, and a dependency
 * would add bundle weight for no benefit. Every operation rejects rather than throwing
 * asynchronously, so the repository can fall back to memory on any failure.
 */

export const DATABASE_NAME = 'arrows';

export class StorageUnavailableError extends Error {
  constructor(cause: string) {
    super(`IndexedDB unavailable: ${cause}`);
    this.name = 'StorageUnavailableError';
  }
}

export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    // Accessing indexedDB can throw in some privacy modes.
    return false;
  }
}

export function openDatabase(name = DATABASE_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new StorageUnavailableError('not supported in this context'));
      return;
    }

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(name, SAVE_SCHEMA_VERSION);
    } catch (error) {
      reject(new StorageUnavailableError(String(error)));
      return;
    }

    request.onupgradeneeded = (event) => {
      const database = request.result;
      runMigrations(
        database,
        request.transaction,
        event.oldVersion,
        event.newVersion ?? SAVE_SCHEMA_VERSION,
      );
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => database.close();
      resolve(database);
    };
    request.onerror = () => reject(new StorageUnavailableError(String(request.error)));
    request.onblocked = () => reject(new StorageUnavailableError('open blocked by another tab'));
  });
}

export type StoreName = (typeof STORE)[keyof typeof STORE];

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

export async function getRecord<T>(
  database: IDBDatabase,
  store: StoreName,
  key: string,
): Promise<T | undefined> {
  const transaction = database.transaction(store, 'readonly');
  return requestToPromise<T | undefined>(transaction.objectStore(store).get(key));
}

export async function putRecord(
  database: IDBDatabase,
  store: StoreName,
  key: string,
  value: unknown,
): Promise<void> {
  const transaction = database.transaction(store, 'readwrite');
  await requestToPromise(transaction.objectStore(store).put(value, key));
  await transactionDone(transaction);
}

export async function deleteRecord(
  database: IDBDatabase,
  store: StoreName,
  key: string,
): Promise<void> {
  const transaction = database.transaction(store, 'readwrite');
  await requestToPromise(transaction.objectStore(store).delete(key));
  await transactionDone(transaction);
}

export async function listKeys(database: IDBDatabase, store: StoreName): Promise<string[]> {
  const transaction = database.transaction(store, 'readonly');
  const keys = await requestToPromise(transaction.objectStore(store).getAllKeys());
  return keys.map((key) => String(key));
}

export async function clearStore(database: IDBDatabase, store: StoreName): Promise<void> {
  const transaction = database.transaction(store, 'readwrite');
  await requestToPromise(transaction.objectStore(store).clear());
  await transactionDone(transaction);
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Transaction aborted'));
  });
}
